import React, { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { getReport, submitFeedback, getMatchMirror } from "@/lib/api";
import Header from "@/components/Header";
import RescanDiffPanel from "@/components/RescanDiffPanel";
import WatchlistStatusBar from "@/components/WatchlistStatusBar";
import AeoPanel from "@/components/AeoPanel";
import { useAuth } from "@/lib/auth";
import { SHOWCASE_REPORT_IDS } from "@/lib/showcase";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, ArrowRight, Sparkles, ThumbsUp, ThumbsDown, Mail, Share2, Copy, Chrome, X } from "lucide-react";

function MatchMirrorSection({ reportId, isLocked, onUnlock }) {
  const [state, setState] = useState({ loading: true, matches: null, error: null });
  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (isLocked) return;
    if (fetchedRef.current) return; // guard against StrictMode double-invoke
    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const data = await getMatchMirror(reportId);
        if (!cancelled) setState({ loading: false, matches: data.matches || [], error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, matches: null, error: "Match & Mirror is temporarily unavailable." });
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, isLocked]);

  // Locked teaser for anonymous viewers — no generation cost until sign-up
  if (isLocked) {
    return (
      <section className="border-b-2 border-[#111]" data-testid="match-mirror-locked">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 text-center">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">🔒 Match &amp; Mirror locked</div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-black leading-tight">
            See who&apos;s already winning in this niche — and mirror them.
          </h2>
          <p className="mt-3 text-sm text-[#333] max-w-xl mx-auto">
            Up to 3 high-performing sites in the same lane, with exactly what they&apos;re doing right and what to replicate. We only show matches that meet our bar.
          </p>
          <button
            onClick={onUnlock}
            className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
            data-testid="unlock-match-mirror-cta"
          >
            🔓 Sign up to unlock <ArrowRight size={14} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="border-b-2 border-[#111]" data-testid="match-mirror-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-center gap-2 mb-3 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
          <Sparkles size={12} className="text-[#FF331F]" /> Comparable market leaders
        </div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
          Match &amp; Mirror
        </h2>

        {state.loading && (
          <div className="mt-6 font-mono-plex text-xs uppercase tracking-[0.25em] text-[#555] p-6 border-2 border-dashed border-[#888] animate-pulse" data-testid="match-mirror-loading">
            ▸ Sourcing comparable market leaders…
          </div>
        )}

        {state.error && (
          <p className="mt-6 font-mono-plex text-sm text-[#666] p-6 border-2 border-dashed border-[#888]">{state.error}</p>
        )}

        {state.matches && state.matches.length === 0 && (
          <div className="mt-6 p-6 border-2 border-dashed border-[#888]" data-testid="match-mirror-empty">
            <p className="font-mono-plex text-xs uppercase tracking-[0.25em] text-[#555]">▸ No qualifying matches at this time</p>
            <p className="mt-2 text-sm text-[#333] max-w-2xl">
              Our bar for Match &amp; Mirror is deliberately high — we only surface sites performing at a level worth replicating. Nothing met that standard for this niche today.
            </p>
          </div>
        )}

        {state.matches && state.matches.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {state.matches.map((m, i) => (
              <div key={i} className="border-2 border-[#111] bg-white p-6 shadow-brutal" data-testid={`match-mirror-card-${i}`}>
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555] border border-[#111] inline-block px-2 py-0.5">
                  Match {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-3 font-serif-display text-2xl font-bold leading-tight">{m.site_name}</h3>
                <div className="font-mono-plex text-[11px] text-[#555]">{m.domain}</div>
                <div className="mt-4 space-y-3 text-sm text-[#333]">
                  <div>
                    <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-[#555]">Why it matches</div>
                    <p className="mt-1">{m.why_it_matches}</p>
                  </div>
                  <div>
                    <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-[#0A8754]">What they&apos;re crushing</div>
                    <p className="mt-1">{m.whats_crushing}</p>
                  </div>
                  <div>
                    <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-[#FF331F]">Mirror this</div>
                    <p className="mt-1 font-semibold">{m.mirror_this}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {state.matches && (
          <p className="mt-6 font-mono-plex text-[9px] uppercase tracking-[0.2em] text-[#888]">
            * Examples surfaced for study. Independent analysis — verify current state before acting.
          </p>
        )}
      </div>
    </section>
  );
}

function scoreColor(score) {
  if (score >= 85) return "#0A8754";
  if (score >= 70) return "#111111";
  return "#F5A623";
}

function ScoreBar({ label, value }) {
  return (
    <div data-testid={`subscore-${label.toLowerCase()}`}>
      <div className="flex justify-between font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">
        <span>{label}</span>
        <span className="text-[#111]">{value}</span>
      </div>
      <div className="mt-1 h-1.5 bg-[#eee] border border-[#111]">
        <div className="h-full bg-[#111]" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
    </div>
  );
}

function FeedbackButtons({ reportId, index }) {
  const [voted, setVoted] = useState(null);
  const vote = async (reaction) => {
    if (voted) return;
    setVoted(reaction);
    try {
      await submitFeedback(reportId, index, reaction);
      toast.success(reaction === "up" ? "Thanks — noted as a good fit." : "Got it — we'll learn from this.");
    } catch (e) {
      setVoted(null);
      toast.error("Couldn't save feedback.");
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid={`feedback-${index}`}>
      <span className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Helpful?</span>
      <button
        onClick={() => vote("up")}
        disabled={!!voted}
        className={`p-2 border-2 transition-colors ${voted === "up" ? "bg-[#0A8754] border-[#0A8754] text-white" : "border-[#111] hover:bg-[#0A8754] hover:border-[#0A8754] hover:text-white"} ${voted && voted !== "up" ? "opacity-30" : ""}`}
        data-testid={`thumbs-up-${index}`}
        aria-label="Thumbs up"
      >
        <ThumbsUp size={14} />
      </button>
      <button
        onClick={() => vote("down")}
        disabled={!!voted}
        className={`p-2 border-2 transition-colors ${voted === "down" ? "bg-[#FF331F] border-[#FF331F] text-white" : "border-[#111] hover:bg-[#FF331F] hover:border-[#FF331F] hover:text-white"} ${voted && voted !== "down" ? "opacity-30" : ""}`}
        data-testid={`thumbs-down-${index}`}
        aria-label="Thumbs down"
      >
        <ThumbsDown size={14} />
      </button>
    </div>
  );
}

function MonetizationCard({ rec, index, reportId, isLocked, totalPlays, onUnlock }) {
  const color = scoreColor(rec.fit_score);
  const [showDetails, setShowDetails] = useState(false);
  // Lock every play except #1 (index 0) for anonymous viewers.
  const cardLocked = isLocked && index > 0;
  return (
    <article
      className="relative bg-white border-2 border-[#111] shadow-brutal p-5 md:p-6 reveal"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`recommendation-${index}`}
    >
      {/* Top row: rank · title · score */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
            No. {String(index + 1).padStart(2, "0")} {cardLocked ? "" : `· ${rec.revenue_model}`}
          </div>
          <h3
            className="mt-1 font-serif-display text-2xl md:text-3xl font-bold tracking-tight leading-tight capitalize"
            style={cardLocked ? { filter: "blur(8px)", userSelect: "none" } : {}}
            aria-hidden={cardLocked}
          >
            {String(rec.monetization_type).replace(/_/g, " ")}
          </h3>
        </div>
        <div className="text-right shrink-0">
          <div
            className="score-display text-4xl md:text-5xl leading-none"
            style={cardLocked ? { color, filter: "blur(10px)", userSelect: "none" } : { color }}
            aria-hidden={cardLocked}
            data-testid={`fit-score-${index}`}
          >
            {rec.fit_score}
          </div>
          <div className="font-mono-plex text-[9px] uppercase tracking-widest text-[#555] mt-0.5">
            fit
          </div>
        </div>
      </div>

      {/* Why it fits */}
      <p
        className="mt-4 font-sans-plex text-sm md:text-[15px] text-[#222] leading-relaxed"
        style={cardLocked ? { filter: "blur(10px)", userSelect: "none" } : {}}
        aria-hidden={cardLocked}
      >
        {cardLocked
          ? "This play is locked. Sign up for free to see why it fits this site, the revenue model, and the full step-by-step implementation plan tailored to this URL."
          : rec.why_it_fits}
      </p>

      {/* Estimated payout */}
      <div
        className="mt-4 font-mono-plex text-[11px] uppercase tracking-widest text-[#111]"
        style={cardLocked ? { filter: "blur(10px)", userSelect: "none" } : {}}
        aria-hidden={cardLocked}
        data-testid={`payout-${index}`}
      >
        ▸ {cardLocked ? "Estimated payout hidden" : rec.estimated_payout}
      </div>

      {/* Next steps — hidden entirely for locked cards */}
      {!cardLocked && (
        <div className="mt-4">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-2">
            ▸ Next steps
          </div>
          <ol className="space-y-1.5">
            {rec.next_steps?.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="font-mono-plex text-xs text-[#FF331F] shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[#222]">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Lock overlay for anonymous viewers on plays #2+ */}
      {cardLocked && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 backdrop-blur-[2px] p-4"
          data-testid={`play-lock-overlay-${index}`}
        >
          <div className="bg-[#111] text-[#F4F4F0] border-2 border-[#111] shadow-brutal px-6 py-5 max-w-sm text-center">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F]">
              🔒 Play locked
            </div>
            <div className="mt-2 font-serif-display text-lg font-bold leading-tight">
              {totalPlays - 1} more {totalPlays - 1 === 1 ? "play" : "plays"} ranked for this URL.
            </div>
            <button
              onClick={onUnlock}
              className="mt-4 inline-flex items-center gap-2 bg-[#FF331F] text-white px-4 py-2 font-mono-plex text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-[#111] transition-colors"
              data-testid={`unlock-play-cta-${index}`}
            >
              🔓 Sign up free <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Collapsible details */}
      {!cardLocked && showDetails && (
        <div className="mt-4 pt-4 border-t border-[#ddd] space-y-4">
          <div>
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-2">
              ▸ Sub-scores
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ScoreBar label="Audience" value={rec.sub_scores.audience} />
              <ScoreBar label="Revenue" value={rec.sub_scores.revenue} />
              <ScoreBar label="Implementation" value={rec.sub_scores.implementation} />
              <ScoreBar label="Evidence" value={rec.sub_scores.evidence} />
            </div>
          </div>
          {rec.suggested_partners?.length > 0 && (
            <div>
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-2">
                ▸ Suggested partners
              </div>
              <ul className="flex flex-wrap gap-1.5">
                {rec.suggested_partners.map((p) => (
                  <li
                    key={p}
                    className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] px-2 py-0.5 bg-[#F4F4F0]"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Footer: details toggle + feedback */}
      {!cardLocked && (
        <div className="mt-4 pt-3 border-t border-dashed border-[#ccc] flex items-center justify-between gap-3">
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] hover:text-[#FF331F]"
            data-testid={`toggle-details-${index}`}
          >
            {showDetails ? "− Hide details" : "+ Sub-scores & partners"}
          </button>
          <FeedbackButtons reportId={reportId} index={index} />
        </div>
      )}
    </article>
  );
}

export default function Results() {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!report);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Email now uses a mailto: link — see the Email button below. No modal / server round-trip needed.

  const shareUrl = typeof window !== "undefined" && report ? `${window.location.origin}/report/${report.id}` : "";
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  // Refetch whenever the report id changes (handles rescan navigation)
  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setLoading(true);
    (async () => {
      try {
        const r = await getReport(id);
        if (!cancelled) setReport(r);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load Blueprint");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm" data-testid="results-loading">
          Loading Blueprint...
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm text-[#FF331F]" data-testid="results-error">
          {error} <Link to="/" className="underline ml-2">Back</Link>
        </div>
      </div>
    );
  }
  if (!report) return null;

  // ============ PREVIEW GATING (visual only — full scan runs + stored regardless) ============
  // Anonymous viewers see: full site metadata + play #1 + blurred/locked plays 2-N + locked API section.
  // Signed-in viewers see everything. Signing up unblurs the report they just generated.
  // Exception: the 5 public Showcase Blueprints are fully visible to everyone (sales proof).
  const isShowcase = SHOWCASE_REPORT_IDS.includes(report.id);
  const isLocked = !user && !isShowcase;
  const unlockCTA = () => {
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grain" data-testid="results-page">
      <Header />

      {/* Showcase conversion strip — anonymous visitors viewing a public sample Blueprint */}
      {isShowcase && !user && (
        <section className="bg-[#111] text-[#F4F4F0] border-b-2 border-[#111]" data-testid="showcase-strip">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-mono-plex text-[10px] uppercase tracking-[0.25em] bg-[#1E40AF] text-white px-2 py-1">Showcase</span>
              <div className="text-sm md:text-base">
                <strong className="font-serif-display">This is a real MonetizeGPS Blueprint — fully unlocked.</strong>{" "}
                Get one for your site: 1 free preview, no signup needed.
              </div>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#FF331F] text-white px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-[#111] transition-colors shrink-0"
              data-testid="showcase-strip-cta"
            >
              Analyze my URL <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {isLocked && (
        <section className="bg-[#111] text-[#F4F4F0] border-b-2 border-[#111]" data-testid="preview-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-mono-plex text-[10px] uppercase tracking-[0.25em] bg-[#FF331F] text-white px-2 py-1">Preview</span>
              <div className="text-sm md:text-base">
                <strong className="font-serif-display">Free preview — Play No. 01 revealed.</strong> Sign up (free) to unlock every play, evidence, and rescan.
              </div>
            </div>
            <button
              onClick={unlockCTA}
              className="inline-flex items-center gap-2 bg-[#FF331F] text-white px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-[#111] transition-colors shrink-0"
              data-testid="preview-unlock-cta"
            >
              🔓 Sign up to unlock <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}

      {/* Masthead */}
      <section className="border-b-2 border-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
              The MonetizeGPS Blueprint
            </span>
            <span className="flex-1 h-px bg-[#111]" />
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
              {new Date(report.created_at).toLocaleString()}
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <a
                href={report.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest text-[#FF331F] hover:underline mb-3"
                data-testid="report-url-link"
              >
                {report.url} <ExternalLink size={12} />
              </a>
              <h1 className="font-serif-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.9]" data-testid="report-title">
                {report.title}
              </h1>
              <p className="mt-4 text-base md:text-lg text-[#333] max-w-2xl leading-relaxed">
                {report.description}
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="border-2 border-[#111] bg-white p-5 relative">
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                  ▸ Executive Summary
                </div>
                <p
                  className={`mt-3 text-sm leading-relaxed ${isLocked ? "select-none" : ""}`}
                  style={isLocked ? { filter: "blur(10px)" } : {}}
                  aria-hidden={isLocked}
                  data-testid="report-summary"
                >
                  {isLocked
                    ? "The executive summary breaks down the site's audience, existing monetization, and the single highest-leverage move you should make. Sign up to unlock this section along with all other plays."
                    : report.overall_summary}
                </p>
                {isLocked && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/40" data-testid="summary-lock-overlay">
                    <button
                      onClick={unlockCTA}
                      className="bg-[#111] text-[#F4F4F0] px-4 py-2 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
                      data-testid="unlock-summary-cta"
                    >
                      🔓 Unlock summary
                    </button>
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    onClick={() => setShareOpen(true)}
                    className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest bg-[#111] text-[#F4F4F0] px-3 py-2 hover:bg-[#FF331F] transition-colors"
                    data-testid="share-button"
                  >
                    <Share2 size={12} /> Share
                  </button>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`MonetizeGPS Blueprint — ${report?.title || report?.url || "URL"}`)}&body=${encodeURIComponent(`Hey — I ran this through MonetizeGPS.\n\nHere's the full monetization Blueprint (top plays, evidence, and next steps):\n\n${shareUrl}\n\nEvery play is scored on audience match, revenue potential, implementation ease, and evidence from the site itself.\n\nSent from MonetizeGPS · https://monetizegps.com`)}`}
                    className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest bg-white text-[#111] border-2 border-[#111] px-3 py-2 hover:bg-[#F4F4F0] transition-colors"
                    data-testid="email-button"
                  >
                    <Mail size={12} /> Email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monitoring status (owner-only) */}
      <WatchlistStatusBar report={report} user={user} />

      {/* Rescan + Diff (owner-only) */}
      <RescanDiffPanel report={report} user={user} />

      {/* Main grid */}
      <section className="border-b-2 border-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Evidence sidebar */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start" data-testid="evidence-panel">
            <div className="border-2 border-[#111] bg-white p-6">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                § Evidence
              </div>
              <h2 className="mt-1 font-serif-display text-3xl font-bold leading-none">
                Detected Signals
              </h2>

              <dl className="mt-6 space-y-4">
                <div>
                  <dt className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Content type</dt>
                  <dd className="mt-1 font-sans-plex">{report.evidence.content_type}</dd>
                </div>
                <div>
                  <dt className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Niche</dt>
                  <dd className="mt-1 font-sans-plex">{report.evidence.niche}</dd>
                </div>
                <div>
                  <dt className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Audience intent</dt>
                  <dd className="mt-1">
                    <span className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] px-2 py-0.5">
                      {report.evidence.audience_intent}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Signals</dt>
                  <dd className="mt-2 flex flex-wrap gap-1.5">
                    {report.evidence.detected_signals?.length ? report.evidence.detected_signals.map((s) => (
                      <span key={s} className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] bg-[#F4F4F0] px-2 py-0.5">
                        {s}
                      </span>
                    )) : <span className="text-xs text-[#666]">None detected.</span>}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">Existing monetization</dt>
                  <dd className="mt-2 flex flex-wrap gap-1.5">
                    {report.evidence.existing_monetization?.length ? report.evidence.existing_monetization.map((s) => (
                      <span key={s} className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] bg-[#111] text-[#F4F4F0] px-2 py-0.5">
                        {s}
                      </span>
                    )) : <span className="text-xs text-[#666]">None detected.</span>}
                  </dd>
                </div>
              </dl>
              <p className="mt-5 pt-4 border-t border-dashed border-[#ccc] text-[11px] text-[#777] leading-relaxed" data-testid="evidence-provenance-note">
                Extracted from the site&apos;s public content at scan time — the same page any
                visitor sees.
              </p>
            </div>
          </aside>

          {/* Recommendations */}
          <div className="lg:col-span-8 space-y-6" data-testid="recommendations-list">
            <div className="flex items-end justify-between border-b-2 border-[#111] pb-3">
              <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
                {report.recommendations.length === 0 ? "Monetization Plays" : `Top ${report.recommendations.length} Plays`}
              </h2>
              <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                Ranked by Fit Score
              </span>
            </div>
            <p className="text-[11px] text-[#777] leading-relaxed -mt-2" data-testid="estimates-disclosure-note">
              Scores reflect fit against site evidence. Revenue figures are directional estimates
              from industry patterns — not measured site data.
            </p>
            {report.recommendations.length === 0 ? (
              <p className="font-mono-plex text-sm text-[#666] p-6 border-2 border-dashed border-[#888]">
                No excellent-fit plays found. The site doesn&apos;t yet show enough commercial intent.
              </p>
            ) : (
              report.recommendations.map((r, i) => <MonetizationCard key={i} rec={r} index={i} reportId={report.id} isLocked={isLocked} totalPlays={report.recommendations.length} onUnlock={unlockCTA} />)
            )}
          </div>
        </div>
      </section>

      {/* APIs — locked for anonymous viewers */}
      {report.api_recommendations?.length > 0 && !isLocked && (
        <section className="border-b-2 border-[#111]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
            <div className="flex items-center gap-2 mb-3 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
              <Sparkles size={12} className="text-[#FF331F]" /> Excellent-fit only
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
              API Plays
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[#111]">
              {report.api_recommendations.map((a, i) => (
                <div
                  key={i}
                  className={`bg-white p-6 ${i % 3 !== 2 ? "md:border-r-2" : ""} ${i < report.api_recommendations.length - (report.api_recommendations.length % 3 || 3) ? "border-b-2" : ""} border-[#111]`}
                  data-testid={`api-rec-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="font-serif-display text-2xl font-bold">{a.name}</h3>
                    <span className="score-display text-3xl" style={{ color: scoreColor(a.fit_score) }}>
                      {a.fit_score}
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-semibold">{a.use_case}</p>
                  <p className="mt-2 text-sm text-[#333]">{a.why_excellent_fit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* API Plays locked teaser for anonymous viewers */}
      {isLocked && report.api_recommendations?.length > 0 && (
        <section className="border-b-2 border-[#111]" data-testid="api-locked-teaser">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 text-center">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">🔒 API Plays locked</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-black leading-tight">
              {report.api_recommendations.length} excellent-fit API {report.api_recommendations.length === 1 ? "play" : "plays"} identified.
            </h2>
            <p className="mt-3 text-sm text-[#333] max-w-xl mx-auto">
              These are specific API integrations that could earn recurring revenue on this URL. Sign up to see which ones and why they fit.
            </p>
            <button
              onClick={unlockCTA}
              className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
              data-testid="unlock-apis-cta"
            >
              🔓 Sign up to unlock <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}

      {/* AEO Readiness — deterministic code checks, separate from the Blueprint */}
      <AeoPanel aeo={report.aeo_audit} isLocked={isLocked} onUnlock={unlockCTA} />

      {/* Match & Mirror — comparable market leaders (locked for anonymous) */}
      <MatchMirrorSection reportId={report.id} isLocked={!user} onUnlock={unlockCTA} />

      {/* CTA — desktop only; Chrome extensions don't work on mobile */}
      <section className="border-b-2 border-[#111] bg-[#111] text-[#F4F4F0] hidden md:block">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <h2 className="font-serif-display text-3xl md:text-5xl font-bold tracking-tight leading-[0.95]">
            Want this for every site you visit?
          </h2>
          <Link
            to="/install"
            className="justify-self-start md:justify-self-end inline-flex items-center gap-3 bg-[#FF331F] text-white px-8 py-5 font-mono-plex text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-[#111] transition-colors shadow-brutal-lg"
            data-testid="install-extension-cta-report"
          >
            <Chrome size={18} /> Get the Chrome extension <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* SHARE MODAL */}
      {shareOpen && (
        <Modal onClose={() => setShareOpen(false)} title="Share this Blueprint" testId="share-modal">
          <p className="text-sm text-[#333] mb-4">Anyone with the link can view this Blueprint.</p>
          <div className="flex items-stretch border-2 border-[#111]">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 px-4 py-3 font-mono-plex text-xs bg-white"
              onClick={(e) => e.target.select()}
              data-testid="share-url-input"
            />
            <button
              onClick={copyShareUrl}
              className="px-4 bg-[#111] text-[#F4F4F0] font-mono-plex text-xs uppercase tracking-widest hover:bg-[#FF331F] flex items-center gap-2"
              data-testid="copy-share-button"
            >
              <Copy size={14} /> Copy
            </button>
          </div>
          <div className="mt-5 flex gap-2 flex-wrap">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I scanned ${report.url} with @monetizegps_ai — here's the monetization plan it generated:`)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noreferrer"
              className="font-mono-plex text-xs uppercase tracking-widest border-2 border-[#111] px-4 py-2 hover:bg-[#111] hover:text-[#F4F4F0]"
              data-testid="share-twitter"
            >Share on X</a>
            <a
              href={`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`I scanned ${report.url} with MonetizeGPS — here's the monetization plan it generated: ${shareUrl}`)}`}
              target="_blank" rel="noreferrer"
              className="font-mono-plex text-xs uppercase tracking-widest border-2 border-[#111] px-4 py-2 hover:bg-[#111] hover:text-[#F4F4F0]"
              data-testid="share-linkedin"
            >Share on LinkedIn</a>
          </div>
        </Modal>
      )}

      {/* Email uses mailto: — see the Email button above. No modal needed. */}
    </div>
  );
}

function Modal({ onClose, title, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="bg-[#F4F4F0] border-2 border-[#111] shadow-brutal-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="font-serif-display text-2xl font-bold leading-none">{title}</h3>
          <button onClick={onClose} className="p-1 hover:text-[#FF331F]" aria-label="Close" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
